// One subject's specifications: every version it has ever had, what each one
// says, and the one button that changes what product is judged against.
//
// ⚠ THIS PAGE ASKS TWO DIFFERENT QUESTIONS AND MUST KEEP THEM APART.
//
//   WHICH LIMITS APPLY   `GET /specs?subjectType=&subjectId=&at=` resolves the
//                        APPROVED version whose window covers an instant. It
//                        never returns a draft. That is rule 6 and it is not
//                        negotiable — an operator measuring against unsigned
//                        limits is the failure this app exists to prevent.
//
//   WHAT AM I SIGNING    `GET /spec-versions?subjectType=&subjectId=` lists every
//                        version whatever its status, and `GET
//                        /spec-versions/{id}` reads one. Review, not resolution.
//
// ⚠ THE SECOND QUESTION HAD NO ANSWER UNTIL 2026-09-19, AND THAT IS WHY THE COLD
// START STALLED. This page used to say, in its own header, that "a draft is never
// served back, so it is approved from the screen that made it" — true for a draft
// you just created and still hold in page state, useless for the 41 drafts
// migrations 004 and 007 loaded. Nobody ever held those responses. The spec
// master listed 45 subjects all reading "none approved", this page offered an
// Approve button with nothing behind it, and there was no path through the UI to
// the thing every other gate is waiting on. Four run-start attempts on 2026-09-17
// ran into the far end of that and stopped.
//
// APPROVE IS A SIGNATURE. It writes ApprovedByUpn and ApprovedAt, takes the
// previous version out of force, and from that moment every reading on every new
// run is measured against these numbers. So the control is disabled unless the
// register says the signed-in person holds SPEC_APPROVE — read from
// dbo.Authorisation, never from a token role — and when the server refuses it
// anyway, the server's own sentence is what is shown. A refusal here is always
// specific: no SPEC_APPROVE grant, the version is not a draft, a later version is
// already in force, the call came through the gateway, or the signer drafted it
// (rule 9, Amit 2 Oct 2026). Replacing any of those with "something went wrong"
// throws away the only part worth reading.
//
// A RAW MATERIAL'S VERSION ALSO SIGNS ITS TERMS: the minimum shelf life on arrival
// and whether a certificate is required live on the version, so they are shown
// beside the item and supplier before the signature. A material set up wrong is
// retired here, never deleted, by someone holding SPEC_APPROVE (never from chat).
//
// THE VERSION ON SCREEN IS IN THE ADDRESS (UI/UX audit, 31 Oct 2026, UX-27):
// `?version=<id>` opens that version, and picking one rewrites it, so a reload or a
// new tab shows the version that was judged rather than whichever the page would
// choose. `?from=` is a way back to the page that linked here (the receipt a
// certificate was judged on), honoured only for a path inside the office.
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { api } from '../../lib/api';
import { AGGREGATION_LABEL, CHECK_KIND_LABEL, SPEC_STATUS_LABEL } from '../../lib/vocabulary';
import { Button } from '@/components/ui/button';
import { Breadcrumb } from '@/components/Breadcrumb';
import { usePageTitle } from '../../lib/pageTitle';
import { DataTable } from '@/components/DataTable';
import { SpecDraftEditor } from './SpecDraftEditor';
import { ReferencePhotosCell, type ReferencePhoto } from './LinePhotos';
import { Saved } from '@/components/Saved';
import type { CheckKind } from './specDraft';
import { currentUpn } from '../../config/auth';
import { TONE_CLASS, chipClass, visibleText } from './goodsInModel';
import { Clamped, Fact, Facts } from '@/components/PageFacts';
import {
  fieldClass,
  grantHint,
  labelClass,
  localDate,
  officeFocus,
  parseSubjectKey,
  serverMessage,
  useMyGrants,
  useResource,
  type MyGrants,
} from '../../shells/OfficeShell';

/** One parameter as the API returns it: the two bands NESTED, never flattened
 *  into one set of four numbers where nobody can tell which is which. */
interface SpecParameter {
  id: number;
  code: string;
  name: string;
  unit: string | null;
  sequence: number;
  checkKind: CheckKind;
  controlPointType: 'NONE' | 'CCP' | 'OPRP';
  /** NUMERIC is measured; ATTRIBUTE is answered yes/no and carries no bands. */
  valueKind?: 'NUMERIC' | 'ATTRIBUTE';
  /** The form's own grouping, e.g. "Fryer Section". Display only. */
  section?: string | null;
  /** The start-up forms' rule: a comment is required when the answer is No. */
  requiresEvidenceOnFail?: boolean;
  intervalMinutes: number | null;
  sampleCount: number;
  aggregation: 'ALL' | 'MEAN' | 'AQS';
  rejectLimits: { min: number | null; max: number | null };
  actionLimits: { lower: number | null; upper: number | null };
  nominal: number | null;
  requiresEvidence: boolean;
  requiresInstrument: boolean;
  instrumentKind: string | null;
  /** A raw material's COFA check (migration 014): what the certificate must read. */
  expectedText?: string | null;
  isMandatory?: boolean;
  /** Its live reference photos (migration 034). Null or absent = not sent: not known (rule 15). */
  referencePhotos?: ReferencePhoto[] | null;
}

/** A row from `GET /spec-versions` — the header of a version, without its
 *  parameters. `parameterCount` is here so the list can say how big a read the
 *  signature is going to be before you open it. */
interface VersionRow {
  id: number;
  subjectType: string;
  subjectId: number;
  version: number;
  status: 'DRAFT' | 'APPROVED' | 'SUPERSEDED';
  effectiveFrom: string | null;
  effectiveTo: string | null;
  approvedByUpn: string | null;
  approvedAt: string | null;
  createdByUpn: string | null;
  notes: string | null;
  parameterCount: number;
  /** A MATERIAL version's own terms (migration 014), signed with it. Null on any other subject. */
  minShelfLifeDays?: number | null;
  cofaRequired?: boolean | null;
  /** The server would let the signed-in drafter approve this draft themselves: they are named in
   *  SPEC_SELF_APPROVERS and a migration loaded it (the SharePoint-approved backlog, Amit 23 Oct
   *  2026; never an app-made draft unless its id is in SPEC_SELF_APPROVE_VERSION_IDS). A hint; the
   *  approve route decides again. */
  selfApprovable?: boolean;
  /** Set when the version was approved by its own drafter, recording a SharePoint approval. */
  selfApproval?: { note: string | null; at: string | null } | null;
}

/** `GET /spec-versions/{id}` */
interface VersionDetail {
  version: VersionRow;
  /** The line's, product's or material's name (UX-20, 31 Oct 2026). Null or absent = not known: the code shows. */
  subjectName?: string | null;
  parameters: SpecParameter[];
  /** A MATERIAL subject: the D365 item and the supplier whose PIF this is. Identity only;
   *  what a certificate is held to is the version's. */
  material?: Material | null;
}

interface Material {
  id: number;
  d365ItemId: string;
  vendorAccount: string | null;
  /** D365's names for the item and the supplier (UX-50). Null = not known: the code alone is shown. */
  itemName?: string | null;
  vendorName?: string | null;
  name: string;
  retiredAt: string | null;
  retiredByUpn: string | null;
  retiredReason: string | null;
}

/** A material set up wrong (a mistyped item, the wrong supplier) is retired, not deleted:
 *  certificates judged against it keep pointing at it. The API refuses it from chat. */
function RetireMaterial({ material, grants, onRetired }: { material: Material; grants: MyGrants; onRetired: () => void }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const hint = grantHint(grants, 'SPEC_APPROVE', 'Retiring a material') ?? (reason.trim() ? null : 'Say what is wrong with it.');

  async function retire() {
    setBusy(true);
    setRefusal(null);
    try {
      await api.post(`/materials/${material.id}/retire`, { reason: reason.trim() });
      onRetired();
    } catch (err: unknown) {
      setRefusal(serverMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <Button variant="ghost" size="sm" type="button" onClick={() => setOpen(true)}>
        Retire this material…
      </Button>
    );
  }
  return (
    <div className="mt-(--s2) flex max-w-[60ch] flex-col gap-(--s2)">
      <label className="flex flex-col gap-(--s1)">
        <span className={labelClass}>What is wrong with this material</span>
        <textarea className={`${fieldClass} ${officeFocus} min-h-[72px] py-(--s2)`} value={reason} maxLength={400} onChange={(e) => setReason(e.target.value)} />
      </label>
      <span title={hint ?? 'Retire this material'}>
        <Button variant="default" type="button" onClick={() => void retire()} disabled={busy || hint !== null}>
          {busy ? 'Retiring…' : 'Retire material'}
        </Button>
      </span>
      <p className="m-0 text-(length:--fs-xs) text-(--muted)">
        {hint ?? 'No certificate is judged against a retired material, and its item and supplier can then be set up again.'}
      </p>
      {refusal ? (
        <p role="alert" className="m-0 text-(length:--fs-sm) font-semibold text-(--warn)">
          {refusal}
        </p>
      ) : null}
    </div>
  );
}

/** `GET /specs?subjectType=&subjectId=` — the resolution answer, kept only for
 *  the one thing it alone can say: which version is IN FORCE right now. */
interface InForcePayload {
  inForce: boolean;
  message?: string | null;
  version: { id: number } | null;
}

function num(v: number | null): string {
  return v === null ? '—' : String(v);
}

/** A band as a person reads it (UI/UX audit, 31 Oct 2026, UX-50): "— – 1.8" became "≤ 1.8", and a
 *  band with neither end one "—". Both ends are inclusive on the line (verdict.ts: OUT only beyond
 *  them). ⚠ `strictMax`: a COFA reject band with a maximum and no minimum is STRICT - equal fails
 *  (rule 21, Anna 2 Oct 2026) - so it reads "< 1.8", never "≤", beside the "max is strict" note. */
export function bandText(lo: number | null, hi: number | null, strictMax = false): string {
  if (lo === null && hi === null) return '—';
  if (lo === null) return `${strictMax ? '<' : '≤'} ${hi}`;
  if (hi === null) return `≥ ${lo}`;
  return `${lo} – ${hi}`;
}

/** The When column's words. UX-16 (31 Oct 2026): a start-up check says so - "READINESS" is the
 *  table's word, not the form's. The other kinds keep their codes until UX-18 words them all. */
// The draft editor's words for every kind, not only READINESS (UI/UX audit, 31 Oct 2026, UX-18):
// When read "FIRST_OFF" and "COFA" beside a header chip that already used words.
const whenText = (kind: CheckKind) => CHECK_KIND_LABEL[kind] ?? kind;

/** Column ids that only mean something for a measured check: unit, both bands, target, samples and
 *  how they are judged. Dropped when every parameter is yes/no (UX-16: LINE-4's 31 start-up checks
 *  each read "—", "— – —", "1" and "ALL"). */
const NUMERIC_COLUMNS = new Set(['unit', 'action', 'reject', 'target', 'sampleCount', 'aggregation']);

/** Only these subjects' checks are made on the line, where a reference photo is looked at (rule 31,
 *  UX-50): a certificate check or a supplier's is never photographed. */
const PHOTO_SUBJECTS = new Set(['PRODUCT', 'LINE', 'AREA']);

/** `?from=`, kept only when it is a path inside the office: never another site, never `//host`. */
export function backPath(from: string | null): string | null {
  if (!from || !from.startsWith('/office/') || from.includes('//') || from.includes('\\')) return null;
  return from;
}

function day(iso: string | null): string {
  if (!iso) return '—';
  return localDate(iso) ?? iso;
}

const needsText = (p: SpecParameter) =>
  [
    p.requiresInstrument ? `instrument${p.instrumentKind ? ` (${p.instrumentKind})` : ''}` : null,
    p.requiresEvidence ? 'evidence' : null,
    p.requiresEvidenceOnFail ? 'comment if No' : null,
    p.valueKind === 'ATTRIBUTE' ? 'yes/no' : null,
    // What a raw material's certificate is held to, shown before it is signed.
    p.expectedText ? `reads "${p.expectedText}"` : null,
    // A lone maximum is strict; the top of a range is inclusive (Anna, 6 Oct 2026).
    p.checkKind === 'COFA' && p.rejectLimits.max !== null && p.rejectLimits.min === null ? 'max is strict (equal fails)' : null,
    p.checkKind === 'COFA' && p.isMandatory === false ? 'optional on the CofA' : null,
  ]
    .filter(Boolean)
    .join(', ');

const pcol = createColumnHelper<SpecParameter>();
const parameterColumns = [
  // Sequence is a spec property, not a sort by id: on a docked iPad the order of
  // the prompts IS the interface, so it is the order the table opens in.
  pcol.accessor('sequence', { header: 'Seq', meta: { align: 'right', mono: true, width: '4rem' } }),
  pcol.accessor((p) => `${p.name} ${p.code} ${p.controlPointType === 'NONE' ? '' : p.controlPointType}`, {
    id: 'parameter',
    header: 'Parameter',
    sortingFn: (a, b) => a.original.name.localeCompare(b.original.name),
    cell: (c) => {
      const p = c.row.original;
      return (
        <>
          {p.name}
          <span className="ml-(--s2) font-mono text-(length:--fs-xs) text-(--muted-2)">
            {p.code}
          </span>
          {p.controlPointType !== 'NONE' ? (
            <span className="ml-(--s2) rounded-(--radius-pill) bg-(--primary-soft) px-(--s2) text-(length:--fs-xs) font-bold">
              {p.controlPointType}
            </span>
          ) : null}
        </>
      );
    },
    meta: { rowHeader: true },
  }),
  // UX-16 (31 Oct 2026): the form's section is a column of its own. Squeezed under "When" it wrapped
  // every start-up row to ~100px ("READINESS / Receival / Flour Milling Section"). Drawn only when a
  // parameter has one.
  pcol.accessor((p) => p.section ?? '', { id: 'section', header: 'Section', cell: (c) => c.getValue() || '—' }),
  pcol.accessor((p) => p.unit ?? '', { id: 'unit', header: 'Unit', cell: (c) => c.getValue() || '—' }),
  // Both bands, side by side and never collapsed into one. The action band is
  // absolute numbers, not a percentage of the reject band — a percentage is one
  // band written twice. Not sortable: parameters in different units do not order.
  pcol.accessor((p) => bandText(p.actionLimits.lower, p.actionLimits.upper), {
    id: 'action',
    header: 'Action band',
    enableSorting: false,
    meta: { mono: true, nowrap: true },
  }),
  pcol.accessor((p) => bandText(p.rejectLimits.min, p.rejectLimits.max, p.checkKind === 'COFA'), {
    id: 'reject',
    header: 'Reject band',
    enableSorting: false,
    meta: { mono: true, nowrap: true },
  }),
  pcol.accessor((p) => num(p.nominal), {
    id: 'target',
    header: 'Target',
    enableSorting: false,
    meta: { align: 'right', mono: true },
  }),
  // A yes/no answer has one sample and nothing to aggregate: "—", not "1" and "ALL" (UX-16).
  pcol.accessor('sampleCount', {
    header: 'Samples',
    cell: (c) => (c.row.original.valueKind === 'ATTRIBUTE' ? '—' : c.getValue()),
    meta: { align: 'right', mono: true },
  }),
  pcol.accessor('aggregation', { header: 'Judged as', cell: (c) => (c.row.original.valueKind === 'ATTRIBUTE' ? '—' : AGGREGATION_LABEL[c.getValue()] ?? c.getValue()) }),
  pcol.accessor((p) => `${whenText(p.checkKind)}${p.intervalMinutes ? ` every ${p.intervalMinutes} min` : ''}`, {
    id: 'when',
    header: 'When',
    cell: (c) => {
      const p = c.row.original;
      return (
        <>
          {whenText(p.checkKind)}
          {p.intervalMinutes ? ` · every ${p.intervalMinutes} min` : ''}
        </>
      );
    },
  }),
  pcol.accessor(needsText, { id: 'needs', header: 'Needs', cell: (c) => c.getValue() || '—' }),
];

/** The reference photos column (migration 034). Rebuilt when who may edit changes: add and retire
 *  are a SPEC_APPROVE holder's, on a DRAFT only; an approved version's photos are as fixed as its limits. */
function photoColumn(canEdit: boolean, onChanged: () => void) {
  return pcol.accessor((p) => (Array.isArray(p.referencePhotos) ? p.referencePhotos.length : -1), {
    id: 'photos',
    header: 'Reference photos',
    enableSorting: false,
    cell: (c) => {
      const p = c.row.original;
      return (
        <ReferencePhotosCell parameterId={p.id} parameterName={p.name} photos={p.referencePhotos} canEdit={canEdit} onChanged={onChanged} />
      );
    },
  });
}

/** Navigation state is whatever the previous page put there — including nothing,
 *  on a reload. Checked rather than cast. */
function draftIdFromState(state: unknown): number | null {
  if (!state || typeof state !== 'object') return null;
  const draft = (state as { draft?: unknown }).draft;
  if (!draft || typeof draft !== 'object') return null;
  const { id } = draft as { id?: unknown };
  return typeof id === 'number' ? id : null;
}

export function SpecVersion() {
  // App.tsx names this parameter `specVersionId`; what it carries is the SUBJECT
  // key — `PRODUCT-12`. Read under the name the route declares, used as what it
  // actually is.
  const { specVersionId } = useParams<{ specVersionId: string }>();
  const subject = parseSubjectKey(specVersionId);
  const location = useLocation();
  const [search, setSearch] = useSearchParams();
  const urlVersion = Number(search.get('version'));
  const versionFromUrl = Number.isInteger(urlVersion) && urlVersion > 0 ? urlVersion : null;
  const back = backPath(search.get('from'));

  const subjectQuery = subject ? `subjectType=${subject.type}&subjectId=${subject.id}` : null;
  const versions = useResource<VersionRow[]>(subjectQuery ? `/spec-versions?${subjectQuery}` : null);
  const inForce = useResource<InForcePayload>(subjectQuery ? `/specs?${subjectQuery}` : null);
  const grants = useMyGrants();

  const [selectedId, setSelectedId] = useState<number | null>(() => versionFromUrl ?? draftIdFromState(location.state));
  /** Picking a version puts it in the address (UX-27), replacing the entry so Back still leaves the page. */
  const pick = (id: number) => {
    setSelectedId(id);
    setSearch(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('version', String(id));
        return next;
      },
      { replace: true },
    );
  };
  const [refusal, setRefusal] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  const [selfNote, setSelfNote] = useState('');

  const rows = useMemo(
    () => [...(versions.data ?? [])].sort((a, b) => b.version - a.version),
    [versions.data],
  );
  const liveId = inForce.data?.inForce ? (inForce.data.version?.id ?? null) : null;

  // ⚠⚠ `liveId === null` MEANS TWO DIFFERENT THINGS, AND CONFLATING THEM PUT A
  // DRAFT ON THIS PAGE AS IF IT WERE THE SPEC IN FORCE.
  //
  // It means "nothing is approved for this subject", and it also means "/specs has
  // not come back yet". The two fetches below are independent, so on a subject
  // with v1 APPROVED and v2 DRAFT the list would routinely arrive first, the
  // effect would fall through to the newest draft, and the `current` guard would
  // then KEEP that draft when /specs finally answered. Which version you were
  // reading came down to network ordering, on the one screen whose entire purpose
  // is knowing what you are about to sign.
  //
  // So the auto-selection waits for an ANSWER, not for a value. An error counts as
  // an answer, because a page with nothing selected is worse than a fallback that
  // says what it could not read.
  //
  // ⚠ THE LATCH IS DELIBERATE: "answered" MUST NEVER GO BACK TO FALSE. `useResource`
  // clears `error` at the top of every refetch but never clears `data`, so from the
  // (data: null, error: set) state a reload reports "no answer yet" all over again.
  // A plain expression therefore flickers false in exactly the situation this page
  // now renders through — /specs failed, the banner is up, the user signs anyway,
  // approve() calls inForce.reload() — and anything gated on it would vanish
  // mid-signature. Once we have heard from /specs, we have heard from it.
  const [inForceAnswered, setInForceAnswered] = useState(false);
  useEffect(() => {
    if (inForce.data !== null || inForce.error !== null) setInForceAnswered(true);
  }, [inForce.data, inForce.error]);

  // What to open when nobody has chosen. The version IN FORCE if there is one,
  // because that is what product is being judged against today. Otherwise the
  // newest DRAFT — which on a cold start is the whole point: 41 subjects have
  // exactly one draft each and nothing in force, and landing on it is the
  // difference between a signature and a dead end.
  useEffect(() => {
    if (rows.length === 0 || !inForceAnswered) return;
    setSelectedId((current) => {
      if (current !== null && rows.some((r) => r.id === current)) return current;
      if (liveId !== null) return liveId;
      // ⚠ /specs COULD NOT BE READ, BUT THE PAGE IS NOT BLIND. /spec-versions
      // returns Status for every version, so an APPROVED row is sitting in `rows`
      // already. Falling straight through to the newest DRAFT here would put
      // unsigned limits on the signature screen whenever the resolution request
      // happened to fail — the same end state this whole guard exists to prevent,
      // reached by a broken request instead of a fast one. An earlier version of
      // this comment claimed an error means "we will never know"; that was simply
      // untrue. We do not know which version is IN FORCE, which is why `isLive`
      // stays false and the banner above says so — but we do know which one was
      // signed, and that is the one to show.
      const approved = rows.find((r) => r.status === 'APPROVED');
      if (approved) return approved.id;
      return (rows.find((r) => r.status === 'DRAFT') ?? rows[0]).id;
    });
  }, [rows, liveId, inForceAnswered]);

  // ⚠ Read only once the id is one of THIS subject's versions (UX-27): an id in the address is typed
  // by anybody, and another subject's version must never render under this subject's title.
  const selectedIsOurs = selectedId !== null && rows.some((r) => r.id === selectedId);
  const detail = useResource<VersionDetail>(selectedIsOurs ? `/spec-versions/${selectedId}` : null);
  /** The address named a version this subject does not have; the page opened its own choice instead. */
  const urlVersionUnknown = versionFromUrl !== null && versions.data !== null && !rows.some((r) => r.id === versionFromUrl);

  const shown = detail.data?.version ?? null;
  // The subject's NAME, not "PRODUCT #27" (UI/UX audit, 31 Oct 2026, UX-20): the row the person clicked
  // said "ALDI OSN Lentil Chips". GET /spec-versions/{id} sends subjectName; not known = the code.
  const subjectTitle =
    detail.data?.subjectName ?? detail.data?.material?.name ?? (subject ? `${subject.type} #${subject.id}` : '');
  usePageTitle(subject ? `${subjectTitle}${shown ? ` · v${shown.version}` : ''}` : null);
  const params = useMemo(
    () => [...(detail.data?.parameters ?? [])].sort((a, b) => a.sequence - b.sequence),
    [detail.data],
  );

  const isLive = shown !== null && shown.id === liveId;
  const isDraft = shown?.status === 'DRAFT';
  const subjectIsProduct = (subject?.type ?? '').toUpperCase() === 'PRODUCT';
  const firstOffCount = params.filter(
    (p) => p.checkKind === 'FIRST_OFF' || p.checkKind === 'BOTH',
  ).length;
  const readinessCount = params.filter((p) => p.checkKind === 'READINESS').length;

  // ⚠ THE FIRST-OFF GATE IS APPLIED AT RUN START, NOT AT APPROVAL, so a product
  // version with no first-off check approves cleanly and then refuses every run.
  // Saying it BEFORE the signature is what stops that being discovered at a 2am
  // changeover. A LINE spec is exempt by nature: it carries READINESS parameters
  // and no first-off ones, and a false warning on an approval screen is worse
  // than none because it teaches people to ignore the true one.
  const wouldRefuseEveryRun = isDraft && subjectIsProduct && firstOffCount === 0;
  // Rule 9 (Amit, 2 Oct 2026): nobody approves a version they drafted. The server refuses it too.
  const ownDraft = isDraft && !!shown?.createdByUpn && shown.createdByUpn.trim().toLowerCase() === currentUpn();
  // The one exception (Amit, 23 Oct 2026): a named self-approver recording a SharePoint approval of
  // a version a migration loaded. It needs a note naming that approval; the server checks too.
  const selfApproving = ownDraft && shown?.selfApprovable === true;
  const noteMissing = selfApproving && visibleText(selfNote) === '';
  // UX-02 (UI/UX audit, 31 Oct 2026): a draft older than the newest APPROVED version cannot be
  // approved - the server refuses it 409 `older_than_in_force`, because approving MATERIAL-15's v1
  // would close v2 and bring back the limits v2 corrected. Read off /spec-versions (every version
  // with its status), not /specs: the server compares against the newest approved row, and the list
  // answers even when the resolution has not. Said in a sentence in place of the button, so nobody
  // reads the draft, finds the note and only then learns it can never be signed.
  const newestApproved = rows.find((r) => r.status === 'APPROVED') ?? null;
  const olderThanInForce = isDraft && shown !== null && newestApproved !== null && shown.version < newestApproved.version;

  const approveHint =
    grantHint(grants, 'SPEC_APPROVE', 'Approving a specification') ??
    (!isDraft
      ? shown
        ? `Version ${shown.version} is ${shown.status.toLowerCase()}, not a draft. Only a draft can be approved.`
        : 'There is no version to approve yet.'
      : ownDraft && !selfApproving
        ? `You drafted version ${shown?.version}. A second person holding SPEC_APPROVE approves it.`
        : noteMissing
        ? `You drafted version ${shown?.version}. To record its approval yourself, name the prior approval in the note.`
        : wouldRefuseEveryRun
        ? 'This draft declares no first-off checks. Approving it would put a specification in ' +
          'force that refuses every run on it.'
        : null);
  const canApprove = isDraft && !olderThanInForce && approveHint === null;

  const canEditPhotos = isDraft && grants.holds('SPEC_APPROVE');
  const reloadDetail = detail.reload;
  // UX-50 (31 Oct 2026): reference photos only where the check is made on the line (rule 31) - a
  // MATERIAL's certificate checks offered "Add reference photo".
  const photosApply = PHOTO_SUBJECTS.has((subject?.type ?? '').toUpperCase());
  const allYesNo = params.length > 0 && params.every((p) => p.valueKind === 'ATTRIBUTE');
  const anySection = params.some((p) => !!p.section);
  const columns = useMemo(
    () => [
      // A column made from a key (`pcol.accessor('sampleCount')`) carries accessorKey, not id.
      ...parameterColumns.filter((c) => {
        const id = c.id ?? (c as { accessorKey?: string }).accessorKey ?? '';
        return !(allYesNo && NUMERIC_COLUMNS.has(id)) && (anySection || id !== 'section');
      }),
      ...(photosApply ? [photoColumn(canEditPhotos, reloadDetail)] : []),
    ],
    [canEditPhotos, reloadDetail, photosApply, allYesNo, anySection],
  );

  async function approve() {
    if (!shown || !isDraft) return;
    setApproving(true);
    setRefusal(null);
    try {
      await api.post(`/specs/${shown.id}/approve`, selfApproving ? { note: selfNote.trim() } : {});
      setSelfNote('');
      versions.reload();
      inForce.reload();
      detail.reload();
    } catch (err: unknown) {
      setRefusal(serverMessage(err));
    } finally {
      setApproving(false);
    }
  }

  // ⚠ "DRAFT A NEW VERSION" USED TO POST AN IDENTICAL COPY AT ONCE, and nothing
  // could then change it: the API creates a draft whole and has no edit route. So
  // no limit could be set in the app at all. It now opens the editor, filled in
  // from the version on screen, and nothing is posted until Save.
  const [editing, setEditing] = useState(false);
  /** Finding 15: what the last save recorded, said where the editor was; gone once a new draft is opened. */
  const [savedNote, setSavedNote] = useState<string | null>(null);

  if (!subject) {
    return (
      <p role="alert" className="m-0 text-(length:--fs-md) font-semibold text-(--warn)">
        That address does not name a subject. A specification is resolved for a subject — a product,
        a line, an area, an asset or a supplier — so the path needs one, for example
        /office/specs/PRODUCT-12. Open it from the spec master.
      </p>
    );
  }

  const loadError = versions.error ?? detail.error;
  if (loadError) {
    return (
      <p role="alert" className="m-0 text-(length:--fs-md) font-semibold text-(--warn)">
        {loadError}
      </p>
    );
  }
  // ⚠ GATED ON THE VERSION LIST ALONE, NOT ON /specs, AND THAT IS THE POINT.
  // Holding the WHOLE page until the resolution answered turned a slow or hung
  // /specs into a blank screen — the version list, the tab bar and the parameter
  // table all withheld because one of two requests had not landed, with no error
  // and no retry. The list is enough to render everything except the SELECTION;
  // that one thing waits, below.
  if (versions.data === null) {
    return <p className="m-0 text-(length:--fs-md) text-(--muted)">Loading…</p>;
  }

  return (
    <div className="flex flex-col gap-(--s5)">
      {/* UX-32 (31 Oct 2026): stacked below md - side by side at 390px the left column was ~90px wide. */}
      <header className="flex flex-col items-stretch gap-(--s4) md:flex-row md:items-start">
        <div className="min-w-0 md:flex-1">
          {back ? (
            <Link to={back} className={`mb-(--s1) inline-block text-(length:--fs-sm) underline ${officeFocus}`}>
              ‹ {back.startsWith('/office/goods-in/') ? 'Back to the receipt' : 'Back'}
            </Link>
          ) : null}
          <Breadcrumb
            items={[
              { label: 'Spec master', to: '/office/specs' },
              { label: `${subjectTitle}${shown ? ` · v${shown.version}` : ''}` },
            ]}
          />
          {/* The material's name when it is one; the status as chips; the terms as facts; the notes folded
              to two lines (header review, 29 Oct 2026: a mono caps sentence, one dotted line and a wall of
              text read as the same grey block). */}
          <h1 className="m-0 mt-(--s1) font-display text-[34px] font-semibold leading-none tracking-[-0.01em]">
            {subjectTitle}
            {shown
              ? ` · v${shown.version}`
              : rows.length === 0
                ? ' · no versions'
                : ' · choosing a version…'}
          </h1>
          {shown ? (
            <p className="m-0 mt-(--s2) flex flex-wrap items-center gap-(--s2) text-(length:--fs-sm) text-(--muted)">
              <span className={`${chipClass} ${TONE_CLASS[shown.status === 'APPROVED' ? 'ok' : shown.status === 'DRAFT' ? 'warn' : 'quiet']}`}>
                {shown.status === 'APPROVED' ? 'Approved' : shown.status === 'DRAFT' ? 'Draft' : shown.status.charAt(0) + shown.status.slice(1).toLowerCase()}
              </span>
              {isLive ? <span className={`${chipClass} ${TONE_CLASS.ok}`}>In force</span> : null}
              {shown.selfApproval ? (
                <span className={`${chipClass} ${TONE_CLASS.quiet}`} title={shown.selfApproval.note ?? undefined}>
                  Self-approved (recorded from SharePoint)
                </span>
              ) : null}
              <span>
                from {day(shown.effectiveFrom)}
                {shown.effectiveTo ? ` to ${day(shown.effectiveTo)}` : ''}
                {shown.approvedByUpn ? ` · approved by ${shown.approvedByUpn} on ${day(shown.approvedAt)}` : ' · not signed'}
              </span>
            </p>
          ) : (
            <p className="m-0 mt-(--s1) text-(length:--fs-sm) text-(--muted)">
              {rows.length === 0 ? 'This subject has no specification versions at all.' : 'Reading which version is in force…'}
            </p>
          )}
          {detail.data?.material && shown ? (
            <>
              <Facts>
                {/* UX-50 (31 Oct 2026): D365's names beside the codes - "SUP000166" and "100150" were all
                    the signer had to go on. A name not known shows the code alone, never a guess. */}
                <Fact label="Item">
                  {detail.data.material.itemName ? `${detail.data.material.itemName} · ` : ''}
                  <span className="font-mono">{detail.data.material.d365ItemId}</span>
                </Fact>
                <Fact label="Supplier">
                  {detail.data.material.vendorAccount ? (
                    <>
                      {detail.data.material.vendorName ? `${detail.data.material.vendorName} · ` : ''}
                      <span className="font-mono">{detail.data.material.vendorAccount}</span>
                    </>
                  ) : (
                    'Any supplier'
                  )}
                </Fact>
                <Fact label="Shelf life on arrival">
                  {detail.data.version.minShelfLifeDays == null ? 'No minimum' : `At least ${detail.data.version.minShelfLifeDays} days`}
                </Fact>
                <Fact label="Certificate">{detail.data.version.cofaRequired === false ? 'Not required' : 'Required'}</Fact>
                <Fact label="Material">#{detail.data.material.id}</Fact>
              </Facts>
              {detail.data.material.retiredAt ? (
                <p className="m-0 mt-(--s2) text-(length:--fs-sm) font-semibold text-(--warn)">
                  Retired by {detail.data.material.retiredByUpn} on {day(detail.data.material.retiredAt)}:{' '}
                  {detail.data.material.retiredReason}. No certificate is judged against it.
                </p>
              ) : null}
            </>
          ) : null}
          {shown?.selfApproval?.note ? <Clamped label="Prior approval" text={shown.selfApproval.note} /> : null}
          {shown?.notes ? <Clamped text={shown.notes} /> : null}
        </div>

        <div className="flex flex-col items-start gap-(--s2) md:items-end">
          {shown && params.length > 0 ? (
            <Button variant="outline" type="button" onClick={() => { setSavedNote(null); setEditing(true); }} disabled={editing}>
              {`Draft a new version from v${shown.version}`}
            </Button>
          ) : null}

          {selfApproving && !olderThanInForce ? (
            <label className="flex w-[340px] max-w-full flex-col gap-(--s1)">
              <span className={labelClass}>Prior approval (you drafted this version)</span>
              <textarea
                className={`${fieldClass} ${officeFocus} min-h-[72px] py-(--s2)`}
                value={selfNote}
                maxLength={2000}
                placeholder="Approved in SharePoint: document and date"
                onChange={(e) => setSelfNote(e.target.value)}
              />
            </label>
          ) : null}

          {/* Approve only where there is a draft to approve: on an approved version it was a disabled
              red button and a sentence saying so (header review, 29 Oct 2026). */}
          {olderThanInForce && shown && newestApproved ? (
            // UX-02 (UI/UX audit, 31 Oct 2026): no button at all, disabled or not - there is nothing
            // a grant or a note could change. The way forward is the button above it.
            <p className="m-0 max-w-[340px] text-(length:--fs-sm) font-semibold text-(--warn) md:text-right">
              Approving v{shown.version} would replace v{newestApproved.version} with these older limits,
              so it cannot be approved. Draft a new version from v{newestApproved.version} instead.
            </p>
          ) : isDraft ? (
          <>
          {/* The title lives on the wrapper: a disabled button does not reliably
              fire a tooltip, and the reason must be reachable with a pointer as
              well as by reading the line below it. */}
          <span title={approveHint ?? `Approve draft v${shown?.version ?? ''}`}>
            <Button
              variant="default"
              type="button"
              onClick={() => void approve()}
              disabled={!canApprove || approving}
              aria-describedby="approve-hint"
            >
              {approving ? 'Approving…' : isDraft ? `Approve draft v${shown?.version}` : 'Approve'}
            </Button>
          </span>
          <p
            id="approve-hint"
            aria-live="polite"
            className="m-0 max-w-[340px] text-(length:--fs-xs) text-(--muted) md:text-right"
          >
            {approveHint ??
              `This signs ${params.length} parameter${params.length === 1 ? '' : 's'} and puts ${params.length === 1 ? 'it' : 'them'} in force from now. Read ${params.length === 1 ? 'it' : 'them'} first.`}
          </p>
          </>
          ) : null}
          {detail.data?.material && !detail.data.material.retiredAt ? (
            <RetireMaterial material={detail.data.material} grants={grants} onRetired={detail.reload} />
          ) : null}
          {!grants.known && grants.error ? (
            <Button variant="outline" type="button" onClick={grants.reload}>
              Re-read my authorisations
            </Button>
          ) : null}
        </div>
      </header>

      {urlVersionUnknown ? (
        <p className="m-0 text-(length:--fs-sm) font-semibold text-(--warn)">
          The address names version #{versionFromUrl}, which is not a version of this subject. The page
          opened {shown ? `v${shown.version}` : 'its own choice'} instead.
        </p>
      ) : null}

      {refusal ? (
        <p
          role="alert"
          className="m-0 rounded-(--radius) border border-(--warn-line) bg-(--warn-bg) p-(--s3) text-(length:--fs-md) font-semibold text-(--warn)"
        >
          {refusal}
        </p>
      ) : null}

      {savedNote && !editing ? <Saved>{savedNote}</Saved> : null}

      {editing && shown ? (
        <SpecDraftEditor
          subjectType={subject.type}
          subjectId={subject.id}
          fromVersion={shown.version}
          initial={params}
          terms={shown}
          isProduct={subjectIsProduct}
          onSaved={(id, note) => {
            setEditing(false);
            setSavedNote(note);
            versions.reload();
            pick(id);
          }}
          onCancel={() => setEditing(false)}
        />
      ) : null}

      {/* Every version, so a draft loaded by a migration is findable at all. Only
          shown when there is more than one: a single row is a tab bar that says
          nothing. */}
      {rows.length > 1 ? (
        <nav className="flex flex-wrap gap-(--s2)" aria-label="Versions">
          {rows.map((r) => (
            <button
              key={r.id}
              type="button"
              aria-pressed={r.id === selectedId}
              onClick={() => pick(r.id)}
              className={`rounded-(--radius) border px-(--s3) py-(--s2) text-(length:--fs-sm) ${officeFocus} ${
                r.id === selectedId
                  ? 'border-(--primary) bg-(--primary-soft) font-semibold'
                  : 'border-(--border) bg-(--card)'
              }`}
            >
              v{r.version} · {SPEC_STATUS_LABEL[r.status] ?? r.status}
              {r.id === liveId ? ' · in force' : ''}
              <span className="ml-(--s2) text-(--muted)">{r.parameterCount}</span>
            </button>
          ))}
        </nav>
      ) : null}

      {/* A draft on screen is unsigned, and the screen has to say so in a sentence
          rather than in a status chip somebody can skim past. */}
      {isDraft ? (
        <p className="m-0 rounded-(--radius) border border-(--border-strong) bg-(--subtle) p-(--s3) text-(length:--fs-sm)">
          <strong>This is a draft.</strong> Nothing is captured against it and no run resolves to it.
          The {params.length} parameter{params.length === 1 ? '' : 's'} below {params.length === 1 ? 'is' : 'are'} what you would be signing
          {shown?.createdByUpn ? `, drafted by ${shown.createdByUpn}` : ''}.
        </p>
      ) : null}

      {wouldRefuseEveryRun ? (
        <p className="m-0 rounded-(--radius) border border-(--warn-line) bg-(--warn-bg) p-(--s3) text-(length:--fs-sm) font-semibold text-(--warn)">
          This version declares no first-off checks, so no run could start against it. A version
          needs at least one parameter checked "{CHECK_KIND_LABEL.FIRST_OFF}" or "{CHECK_KIND_LABEL.BOTH}".
        </p>
      ) : null}

      {/* The mirror of the warning above, for a line. A line spec with no
          readiness items is legal and starts runs cleanly — it just asks
          nothing, so nothing is ever blocked. That is a quiet failure worth
          naming, because it looks identical to a line that passed. A LINE only:
          on a raw material it is a false warning on the signature screen. */}
      {shown && subject.type === 'LINE' && readinessCount === 0 && params.length > 0 ? (
        <p className="m-0 rounded-(--radius) border border-(--warn-line) bg-(--warn-bg) p-(--s3) text-(length:--fs-sm) font-semibold text-(--warn)">
          This version declares no start-up checks, so runs on this line are never held for one.
          That is not the same as a line that passed — nothing is being asked.
        </p>
      ) : null}

      {/* ⚠ "NOTHING IS IN FORCE" IS A CLAIM, AND IT NEEDS THE ANSWER TO MAKE IT.
          When /specs fails, `inForce.data` is null and the old condition printed
          that sentence anyway — asserting no approved specification exists on the
          strength of a request that never came back. Failing to read the
          resolution and there being nothing to read are different states and the
          screen has to say which one it is. */}
      {inForce.error ? (
        <p
          role="alert"
          className="m-0 rounded-(--radius) border border-(--warn-line) bg-(--warn-bg) p-(--s3) text-(length:--fs-sm) font-semibold text-(--warn)"
        >
          Could not read which version is in force: {inForce.error} What is shown below is a
          version of this specification, not necessarily the one product is being judged against.
        </p>
      ) : !inForce.data?.inForce ? (
        <p className="m-0 text-(length:--fs-sm) text-(--muted)">
          Nothing is in force for this subject right now, so no run can start against it.
        </p>
      ) : null}

      {rows.length === 0 ? (
        <p className="m-0 text-(length:--fs-md) text-(--muted)">
          This subject has no specification versions at all — not even a draft.
        </p>
      ) : shown === null ? (
        /* Versions exist; which one to open is still being decided, because the
           auto-selection waits for /specs. The tab bar above is already live, so
           this is a placeholder for one panel rather than a blank page — pick a
           version yourself and it opens immediately. */
        // "Pick a version above" only when there IS a picker above (UI/UX audit, 31 Oct 2026, UX-10):
        // the tab bar renders for more than one version, and with one the sentence pointed at nothing.
        <p className="m-0 text-(length:--fs-md) text-(--muted)">
          Reading which version is in force, so the right one opens.
          {rows.length > 1 ? ' Pick a version above to read it now.' : ''}
        </p>
      ) : (
        <>
        {!isDraft && photosApply ? (
          <p className="m-0 text-(length:--fs-sm) text-(--muted)">Reference photos are added on a draft.</p>
        ) : null}
        <DataTable
          key={shown.id}
          data={params}
          columns={columns}
          caption="Parameters in the order the operator sees them"
          getRowId={(p) => String(p.id)}
          search={params.length > 10 ? 'Search parameter, code or section' : undefined}
          loading={detail.loading}
          empty="This version has no active parameters, so it expects nothing of anybody."
        />
        </>
      )}
    </div>
  );
}

export default SpecVersion;
